// src/hooks/useClientPagination.js
//
// Shared client-side pagination hook
// =================================
//
// PURPOSE
// -------
// This hook centralizes the standard client-side pagination logic used by
// overview/table pages.
//
// It does NOT fetch data.
// It only paginates an already available array.
//
// Typical use cases:
// - visits table
// - vouchers overview
// - products overview
// - services overview
// - reports with client-side row slicing
//
// CONTRACT
// --------
// Official returned page items field:
//
//   paginatedItems
//
// Do not use alternative names such as:
// - pagedItems
// - pageItems
// - currentItems
// - items
//
// Keeping one explicit contract avoids runtime errors when pages destructure
// the hook result.
//
// DESIGN PRINCIPLES
// -----------------
// - Defensive against non-array input.
// - Keeps current page inside valid range when data length changes.
// - Resets to page 1 when page size changes.
// - Does not persist page size itself. Persistence belongs to the page because
//   each page may want its own localStorage key.
// - Works with simple arrays only. Server-side pagination should use a separate
//   hook later.

import { useCallback, useEffect, useMemo, useState } from "react";

function normalizePositiveInteger(value, fallback) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function useClientPagination(items, options = {}) {
  const safeItems = Array.isArray(items) ? items : [];

  const initialPageSize = normalizePositiveInteger(
    options.initialPageSize,
    50
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const totalRows = safeItems.length;

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(totalRows / pageSize));
  }, [totalRows, pageSize]);

  /**
   * Keep page within the valid range whenever:
   * - data length changes,
   * - page size changes,
   * - filters reduce the number of rows.
   */
  useEffect(() => {
    setPage((current) => {
      if (current < 1) return 1;
      if (current > pageCount) return pageCount;
      return current;
    });
  }, [pageCount]);

  const startIndex = useMemo(() => {
    return (page - 1) * pageSize;
  }, [page, pageSize]);

  const endIndex = useMemo(() => {
    return startIndex + pageSize;
  }, [startIndex, pageSize]);

  const paginatedItems = useMemo(() => {
    return safeItems.slice(startIndex, endIndex);
  }, [safeItems, startIndex, endIndex]);

  const canPrev = page > 1;
  const canNext = page < pageCount;

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  const goPrev = useCallback(() => {
    setPage((current) => Math.max(1, current - 1));
  }, []);

  const goNext = useCallback(() => {
    setPage((current) => Math.min(pageCount, current + 1));
  }, [pageCount]);

  /**
   * Page size setter.
   *
   * Important:
   * Changing page size should normally return to page 1 because the currently
   * selected page may no longer represent the same logical row position.
   */
  const setPageSize = useCallback((nextPageSize) => {
    const normalized = normalizePositiveInteger(nextPageSize, initialPageSize);

    setPageSizeState(normalized);
    setPage(1);
  }, [initialPageSize]);

  return {
    page,
    pageSize,
    pageCount,
    totalRows,

    startIndex,
    endIndex,

    paginatedItems,

    setPage,
    setPageSize,
    resetPage,
    goPrev,
    goNext,

    canPrev,
    canNext,
  };
}